import { defineStore } from 'pinia'
import { api } from '@/boot/axios'
import { localDb } from '@/services'

/** Laravel Sanctum prefix is `api/sanctum` (see blu-backend config/sanctum.php). */
async function ensureSanctumCsrfCookie () {
  await api.get('/sanctum/csrf-cookie')
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    isAuthenticated: false,
    user: null,
    lastAuthCheck: null,
    authCheckInterval: 5 * 60 * 1000, // 5 minutes in milliseconds
    needsPasswordReset: false,
    fetchUserPromise: null,
    checkAuthPromise: null,
    sessionCheckPromise: null,
    lastSessionCheckAt: null,
    lastSessionCheckResult: null,
    sessionCheckCacheMs: 10 * 1000,
    // Phase 1 offline support: indicates we are using cached data.
    isOffline: false
  }),

  actions: {
    async hydrateFromCache() {
      try {
        const [cachedUser, cachedSession] = await Promise.all([
          localDb.cacheGetUser(),
          localDb.cacheGetSession()
        ])

        const offline = typeof navigator !== 'undefined' && navigator.onLine === false
        this.isOffline = offline

        if (cachedUser) {
          this.user = cachedUser
          this.isAuthenticated = true
          this.needsPasswordReset = cachedUser.needs_password_reset ?? false
          // We intentionally do not treat this as a verified session.
          this.lastAuthCheck = cachedSession?.checkedAt || this.lastAuthCheck || Date.now()
          return true
        }
      } catch (e) {
        console.warn('Failed to hydrate auth cache from local DB:', e)
      }
      return false
    },

    async hasActiveSession() {
      const now = Date.now()
      if (
        this.lastSessionCheckAt != null &&
        this.lastSessionCheckResult != null &&
        (now - this.lastSessionCheckAt) < this.sessionCheckCacheMs
      ) {
        return this.lastSessionCheckResult
      }

      if (this.sessionCheckPromise) {
        return this.sessionCheckPromise
      }

      this.sessionCheckPromise = this.probeActiveSession()
      try {
        return await this.sessionCheckPromise
      } finally {
        this.sessionCheckPromise = null
      }
    },

    async probeActiveSession() {
      try {
        // If offline, fall back to cached session/user for Phase 1 read-only mode.
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          const [cachedSession, cachedUser] = await Promise.all([
            localDb.cacheGetSession(),
            localDb.cacheGetUser()
          ])
          this.isOffline = true
          const authenticated = !!cachedSession?.authenticated && !!cachedUser
          this.lastSessionCheckAt = Date.now()
          this.lastSessionCheckResult = authenticated
          return authenticated
        }

        const response = await api.get('/auth/check', { meta: { silent: true } })
        try {
          await localDb.cacheSetSession({
            authenticated: !!response.data.authenticated,
            checkedAt: Date.now()
          })
        } catch (e) {
          console.warn('Failed to persist session cache to local DB:', e)
        }
        const authenticated = !!response.data.authenticated
        this.lastSessionCheckAt = Date.now()
        this.lastSessionCheckResult = authenticated
        return authenticated
      } catch (error) {
        // If we get a 401, it means we're not authenticated
        if (error.response?.status === 401) {
          try {
            await localDb.cacheSetSession({ authenticated: false, checkedAt: Date.now() })
          } catch {
            // ignore
          }
          this.lastSessionCheckAt = Date.now()
          this.lastSessionCheckResult = false
          return false
        }
        // If there's no HTTP response, treat as offline/network error and try cache.
        if (!error.response) {
          try {
            const ok = await this.hydrateFromCache()
            if (ok) {
              this.lastSessionCheckAt = Date.now()
              this.lastSessionCheckResult = true
              return true
            }
          } catch {
            // ignore
          }
        }
        // For other errors, we can't determine auth status, so assume not authenticated
        console.error('Auth check error:', error)
        this.lastSessionCheckAt = Date.now()
        this.lastSessionCheckResult = false
        return false
      }
    },

    async login(email, password) {
      await ensureSanctumCsrfCookie()
      const response = await api.post('/auth/login', { email, password })
      this.needsPasswordReset = response.data?.needs_password_reset ?? false
      this.isAuthenticated = true
      this.lastSessionCheckAt = Date.now()
      this.lastSessionCheckResult = true
      await this.fetchUser()
    },

    async setPassword(password) {
      const response = await api.post('/auth/set-password', {
        password, password_confirmation: password
      })
      this.needsPasswordReset = false
      await this.fetchUser()
      return response.data
    },

    async requestPasswordReset(email) {
      await ensureSanctumCsrfCookie()
      const response = await api.post('/auth/forgot-password', { email })
      return response.data
    },

    async resetPassword(token, email, password) {
      await ensureSanctumCsrfCookie()
      const response = await api.post('/auth/reset-password', {
        token,
        email,
        password,
        password_confirmation: password
      })
      this.needsPasswordReset = response.data?.needs_password_reset ?? false
      this.isAuthenticated = true
      this.lastSessionCheckAt = Date.now()
      this.lastSessionCheckResult = true
      await this.fetchUser()
      return response.data
    },

    async logout() {
      try {
        // Only try if we're actually logged in currently
        if (!this.isAuthenticated) {
          return
        }

        this.isAuthenticated = false
        this.user = null
        this.lastAuthCheck = null
        this.lastSessionCheckAt = Date.now()
        this.lastSessionCheckResult = false
        try {
          await localDb.cacheSetUser(null)
          await localDb.cacheSetSession({ authenticated: false, checkedAt: Date.now() })
        } catch {
          // ignore
        }
        try {
          if (typeof window !== 'undefined') {
            window.localStorage.removeItem('blu_locale')
          }
        } catch {
          // ignore
        }

        try {
          const { clearPosModifierSessionCache } = await import('@/utils/posModifierSessionCache')
          clearPosModifierSessionCache()
        } catch {
          // ignore
        }

        await api.post('/auth/logout')
      } catch (error) {
        console.error('Error during logout:', error)
      }
    },


    /**
     * Merge profile data into user and update IndexedDB cache.
     * Call after profile update so auth cache stays in sync.
     */
    async mergeUserFromProfile(profile) {
      if (!this.user || !profile) return
      const merged = { ...this.user }
      for (const key of Object.keys(profile)) {
        if (profile[key] !== undefined) merged[key] = profile[key]
      }
      this.user = merged
      try {
        await localDb.cacheSetUser(merged)
      } catch (e) {
        console.warn('Failed to update user cache after profile merge:', e)
      }
    },

    async fetchUser() {
      if (this.fetchUserPromise) return this.fetchUserPromise
      this.fetchUserPromise = (async () => {
        const response = await api.get('/user')
        this.isAuthenticated = true
        this.user = response.data
        this.needsPasswordReset = response.data.needs_password_reset ?? false
        this.lastAuthCheck = Date.now()
        try {
          await localDb.cacheSetUser(response.data)
          await localDb.cacheSetSession({
            authenticated: true,
            checkedAt: Date.now()
          })
        } catch (e) {
          console.warn('Failed to persist user cache to local DB:', e)
        }
        return response.data
      })()
      try {
        return await this.fetchUserPromise
      } finally {
        this.fetchUserPromise = null
      }
    },

    async checkAuth(force = false) {
      if (!force && this.checkAuthPromise) return this.checkAuthPromise
      const now = Date.now()
      const needsCheck = force || !this.lastAuthCheck || (now - this.lastAuthCheck > this.authCheckInterval)

      // Phase 1 offline behavior: if offline, rely on cached auth/user for read-only.
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        const ok = await this.hydrateFromCache()
        if (ok) return
        throw new Error('Offline and no cached session')
      }

      // If we're not authenticated, always check
      if (!this.isAuthenticated) {
        this.checkAuthPromise = (async () => {
          try {
            await this.fetchUser()
          } catch (error) {
            if (error.response?.status !== 401) {
              console.error('Auth check failed:', error)
            }
            this.isAuthenticated = false
            this.user = null
            this.needsPasswordReset = false
            this.lastAuthCheck = null
            this.isOffline = false
            throw error
          }
        })()
        try {
          return await this.checkAuthPromise
        } finally {
          this.checkAuthPromise = null
        }
      }

      // If we are authenticated, only check periodically
      if (!needsCheck) {
        return
      }

      this.checkAuthPromise = (async () => {
        try {
          await this.fetchUser()
        } catch (error) {
          console.error('Auth check failed:', error)
          this.isAuthenticated = false
          this.user = null
          this.needsPasswordReset = false
          this.lastAuthCheck = null
          this.isOffline = false
          throw error
        }
      })()
      try {
        return await this.checkAuthPromise
      } finally {
        this.checkAuthPromise = null
      }
    },
  }
})
